import React from 'react';
import { Alert, Box, Button, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getScorecard } from '../api/botOpsService';
import { useQuery } from '../hooks/useQuery';
import { LeaderRow, ScoreStats } from '../lib/types';
import { fmtLatency } from '../lib/activity';
import { fmtIct } from '../lib/ict';
import { FONT, OVERLINE, RADIUS, TABLE_HEAD_SX, TEAL, TONE } from '../lib/brand';

const pct = (v: number | null) => (v == null ? '—' : `${v}%`);
const nd = (n: number, d: number) => `${n} / ${d}`;

const LEADER_METRICS = [
  { key: 'recommendations', label: 'Recommendations' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'agreed_pct', label: 'Agreed %' },
  { key: 'events_7d', label: 'Work events (7 d)' },
  { key: 'documents', label: 'Documents' },
] as const;
type LeaderKey = (typeof LEADER_METRICS)[number]['key'];

/** Top 15 by a metric. Rows with no value for it (agreed % needs at least one review) are left out, not ranked as 0. */
export function topBy(rows: LeaderRow[], key: LeaderKey, n = 15): LeaderRow[] {
  return rows
    .filter((r) => r[key] != null && (key !== 'agreed_pct' || r.reviewed >= 1))
    .slice()
    .sort((a, b) => ((b[key] as number) - (a[key] as number)) || (a.name < b.name ? -1 : 1))
    .slice(0, n);
}

const DerivedTip = ({ text }: { text: string }) => (
  <Tooltip title={text} arrow>
    <Box component="span" role="note" tabIndex={0} aria-label={`Derived. ${text}`} sx={{ ml: 0.5, fontSize: 9, fontWeight: 700, px: 0.5, borderRadius: RADIUS.sm, bgcolor: TONE.neutral.bg, color: TONE.neutral.fg, cursor: 'help' }}>DERIVED</Box>
  </Tooltip>
);

function Row({ label, s }: { label: string; s: ScoreStats }) {
  return (
    <TableRow data-testid={`score-${label}`}>
      <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
      <TableCell>{s.recommendations}</TableCell>
      <TableCell>{nd(s.reviewed, s.recommendations)}</TableCell>
      <TableCell>{pct(s.agreed_pct)} <Typography component="span" variant="caption" color="text.secondary">({nd(s.agreed, s.reviewed)})</Typography></TableCell>
      <TableCell>{pct(s.approved_pct)} <Typography component="span" variant="caption" color="text.secondary">({nd(s.approved, s.recommendations)})</Typography></TableCell>
      <TableCell>{pct(s.implemented_pct)} <Typography component="span" variant="caption" color="text.secondary">({nd(s.done, s.approved)})</Typography></TableCell>
      <TableCell>{fmtLatency(s.latency_minutes.rec_to_verdict)}</TableCell>
      <TableCell>{fmtLatency(s.latency_minutes.verdict_to_grant)}</TableCell>
      <TableCell>{fmtLatency(s.latency_minutes.grant_to_done)}</TableCell>
    </TableRow>
  );
}

const Trend = ({ points, label }: { points: { date: string; new_recommendations: number }[]; label: string }) => {
  if (points.length < 2) return <Typography variant="caption" color="text.secondary">{label}: no trend yet (needs two dates).</Typography>;
  const max = Math.max(...points.map((p) => p.new_recommendations), 1);
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Box role="img" aria-label={`New recommendations per date, ${label}`} sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 48 }}>
        {points.map((p) => (
          <Tooltip key={p.date} title={`${p.date}: ${p.new_recommendations}`} arrow describeChild>
            <Box sx={{ flex: 1, maxWidth: 28, height: `${Math.max(6, (p.new_recommendations / max) * 100)}%`, bgcolor: TEAL, borderRadius: '2px' }} />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

/** Team scorecard: how recommendations move from made, to reviewed, to approved, to done, and how long each step takes. */
export default function ScorecardPage() {
  const { data, error, loading, reload } = useQuery(getScorecard, []);
  const [metric, setMetric] = React.useState<LeaderKey>('recommendations');

  if (!data) {
    return (
      <Box sx={{ maxWidth: 1480, mx: 'auto', p: 3 }}>
        {error ? <Alert severity="error" role="alert" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>Could not load the scorecard: {error.message}</Alert> : null}
        {loading && !error ? <Skeleton variant="rounded" height={240} data-testid="score-loading" /> : null}
      </Box>
    );
  }

  const top = topBy(data.leaderboard, metric);
  const NAMES: Record<string, string> = { lifescribe: 'LifeScribe', quill: 'Quill' };
  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <Typography component="h1" sx={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 700, color: 'primary.main', mb: 0.4 }}>Team scorecard</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>As of {fmtIct(data.generated_ict)}. Approved and implemented follow Grant&apos;s live decisions. A stage with nothing to measure reads “no data”, never 0.</Typography>
      {error && <Alert severity="warning" role="alert" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>Could not refresh: {error.message}. Showing the last loaded numbers.</Alert>}

      <Box sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small" aria-label="Scorecard" sx={{ minWidth: 900 }}>
          <TableHead sx={TABLE_HEAD_SX}>
            <TableRow>
              <TableCell />
              <TableCell>Recommendations</TableCell>
              <TableCell>Reviewed</TableCell>
              <TableCell>Agreed</TableCell>
              <TableCell>Approved</TableCell>
              <TableCell>Implemented</TableCell>
              <TableCell>Rec → verdict<DerivedTip text={data.basis} /></TableCell>
              <TableCell>Verdict → Grant<DerivedTip text={data.basis} /></TableCell>
              <TableCell>Grant → done<DerivedTip text={data.basis} /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(['lifescribe', 'quill'] as const).map((c) => (data.companies[c] ? <Row key={c} label={NAMES[c]} s={data.companies[c] as ScoreStats} /> : null))}
            <TableRow><TableCell colSpan={9} sx={{ ...OVERLINE, bgcolor: 'action.hover', fontSize: 11 }}>BY REVIEWING EXEC</TableCell></TableRow>
            {Object.keys(data.execs).map((e) => <Row key={e} label={e} s={data.execs[e]} />)}
          </TableBody>
        </Table>
      </Box>

      <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700, mb: 1 }}>Leaderboard</Typography>
      <ToggleButtonGroup exclusive size="small" value={metric} aria-label="Leaderboard metric" onChange={(_e, v: LeaderKey | null) => v && setMetric(v)} sx={{ mb: 1, flexWrap: 'wrap' }}>
        {LEADER_METRICS.map((m) => <ToggleButton key={m.key} value={m.key} sx={{ textTransform: 'none' }}>{m.label}</ToggleButton>)}
      </ToggleButtonGroup>
      {top.length === 0 ? <Typography variant="body2" color="text.secondary">No bot has a value for this yet.</Typography> : (
        <Table size="small" aria-label="Leaderboard" sx={{ maxWidth: 640, mb: 3 }}>
          <TableBody>
            {top.map((b, i) => (
              <TableRow key={b.slug} hover data-testid="leader-row">
                <TableCell sx={{ width: 36, color: 'text.secondary' }}>{i + 1}</TableCell>
                <TableCell><RouterLink to={`/dashboard/bot-ops/bots/${encodeURIComponent(b.slug)}`}>{b.name}</RouterLink> <Typography component="span" variant="caption" color="text.secondary">{NAMES[b.company]}</Typography></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{metric === 'agreed_pct' ? pct(b.agreed_pct) : b[metric]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 16, fontWeight: 700, mb: 1 }}>New recommendations per date</Typography>
      {(['lifescribe', 'quill'] as const).map((c) => <Trend key={c} label={NAMES[c]} points={(data.trend[c] || [])} />)}
    </Box>
  );
}
